import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import AddAffiliateModal from './AddAffiliateModal';
import { affiliateService } from '../../services/affiliateService';

interface AffiliatesListProps {
  onAffiliateSelect: (affiliateId: string) => void;
  programId?: string;
}

interface Affiliate {
  id: string;
  contact_name?: string;
  company_name?: string;
  email: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  total_sales?: number;
  total_commission?: number;
  conversion_rate?: number;
  custom_commission_rate?: number | null;
  website?: string;
  notes?: string;
}



const AffiliatesList: React.FC<AffiliatesListProps> = ({ onAffiliateSelect, programId }) => {
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch affiliates from API
  const fetchAffiliates = async () => {
    if (!programId) {
      setError('No program ID provided');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);

      const data = await affiliateService.listAffiliates(programId, statusFilter === 'all' ? undefined : statusFilter);
      setAffiliates((data as any)?.affiliates || []);
    } catch (error) {
      console.error('Error fetching affiliates:', error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
      toast.error('Failed to load affiliates');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchAffiliates();
  }, [programId, statusFilter]);

  const handleStatusChange = async (affiliateId: string, newStatus: 'pending' | 'approved' | 'rejected') => {
    if (!programId) {
      toast.error('No program ID provided');
      return;
    }
    
    try {
      await affiliateService.updateAffiliateStatus(programId, affiliateId, newStatus);
      
      // Update local state
      const updatedAffiliates = affiliates.map(affiliate => {
        if (affiliate.id === affiliateId) {
          return { ...affiliate, status: newStatus };
        }
        return affiliate;
      });
      
      setAffiliates(updatedAffiliates);
      toast.success(`Affiliate status updated to ${newStatus}`);
      
    } catch (error) {
      console.error('Error updating affiliate status:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update affiliate status');
    }
  };

  const filteredAffiliates = affiliates.filter(affiliate => {
    const matchesSearch = 
      (affiliate.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
      (affiliate.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
      affiliate.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || affiliate.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 my-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        {/* Add Affiliate Button */}
        <div className="flex justify-end mb-4">
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Add Affiliate
          </button>
        </div>

        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Affiliates List</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              View and manage all your affiliates.
            </p>
          </div>
        </div>
        
        {/* Search and Filter Controls */}
        <div className="px-4 py-5 sm:px-6 flex flex-wrap gap-4 items-center justify-between bg-gray-50 border-t border-b border-gray-200">
          <div className="w-full md:w-auto">
            <label htmlFor="search" className="sr-only">Search</label>
            <input
              type="text"
              id="search"
              name="search"
              placeholder="Search by name or email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
            />
          </div>
          
          <div className="w-full md:w-auto flex items-center">
            <label htmlFor="status-filter" className="mr-2 text-sm font-medium text-gray-700">Status:</label>
            <select
              id="status-filter"
              name="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border-gray-300 rounded-md"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {/* Affiliates Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Affiliate</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Joined</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Sales</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commission</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conv. Rate</th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAffiliates.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                    No affiliates found
                  </td>
                </tr>
              ) : (
                filteredAffiliates.map((affiliate) => (
                  <tr key={affiliate.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{affiliate.contact_name || affiliate.company_name || 'Unnamed Affiliate'}</div>
                          <div className="text-sm text-gray-500">{affiliate.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${affiliate.status === 'approved' ? 'bg-green-100 text-green-800' : 
                          affiliate.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-red-100 text-red-800'}`}>
                        {affiliate.status.charAt(0).toUpperCase() + affiliate.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(affiliate.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      ${(affiliate.total_sales || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      ${(affiliate.total_commission || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {(affiliate.conversion_rate || 0).toFixed(1)}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button 
                        className="text-blue-600 hover:text-blue-900 mr-4"
                        onClick={() => onAffiliateSelect(affiliate.id)}
                      >
                        View
                      </button>
                      <select
                        value={affiliate.status}
                        onChange={(e) => handleStatusChange(affiliate.id, e.target.value as 'pending' | 'approved' | 'rejected')}
                        className="text-xs border-gray-300 rounded-md"
                      >
                        <option value="approved">Set Approved</option>
                        <option value="pending">Set Pending</option>
                        <option value="rejected">Set Rejected</option>
                      </select>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination - placeholder */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
              Previous
            </button>
            <button className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">1</span> to <span className="font-medium">{filteredAffiliates.length}</span> of <span className="font-medium">{filteredAffiliates.length}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                  Previous
                </button>
                <button className="bg-white border-gray-300 text-gray-500 hover:bg-gray-50 relative inline-flex items-center px-4 py-2 border text-sm font-medium">
                  1
                </button>
                <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Add Affiliate Modal */}
      {isAddModalOpen && programId && (
        <AddAffiliateModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          programId={programId}
          onAffiliateSaved={fetchAffiliates}
        />
      )}
    </div>
  );
};

export default AffiliatesList;
